import React from "react";
import { type RouteComponentProps, useLocation } from "react-router-dom";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faEye } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faExclamationCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";

import { ConnectivityProvider, LoadMoreButton } from "~/common";
import { CustomEmojiManager } from "~/common/Emoji";
import { ReactComponent as AccessDenied } from "~/legacy-assets/images/icons/access-denied.svg";
import WebsocketClient from "~/lib/websocket";
import Header from "~/public/Header";
import Loader from "~/public/Loader";
import Note, { type FellowNote } from "~/public/Note";
import NotFound from "~/public/NotFound";
import type { StreamType } from "~/stream/types";
import Popup from "~/ui/legacy/Popup";
import { ButtonLink, ContactSupportLink, HelpCenterLink } from "~/ui/legacyFellowIndex";

import { NoteBlur } from "./NoteBlur";

import paywallStyles from "~/billing/css/Billing.module.css";

import styles from "./css/PublicStream.module.css";

interface StreamHistory {
	paywalled: boolean;
	limit?: number;
	paywallDate?: string;
}

interface FellowAccount {
	name: string;
	logo: string;
	id: string;
}

interface FellowStream {
	title: {
		text: string;
	};
	type: StreamType;
	notes: FellowNote[];
	hasNotesRemaining: boolean;
	hasSafeBrowsingThreats: boolean;
	history: StreamHistory;
	account: FellowAccount;
}

type StreamProps = RouteComponentProps<{ streamId: string }>;

export default function Stream(props: StreamProps) {
	const [loading, setLoading] = React.useState(true);
	const [stream, setStream] = React.useState<FellowStream | null>(null);
	const [offset, setOffset] = React.useState(0);
	const [acceptedRisk, setAcceptedRisk] = React.useState(false);
	const location = useLocation();

	const inviteId = new URLSearchParams(location.search).get("stream_invite") || undefined;

	const fetchNotes = React.useCallback(
		(callback?: () => void) => {
			const formData = new FormData();

			formData.append("method", "stream_notes");
			formData.append("offset", offset.toString());
			formData.append("limit", "5");

			fetch("", {
				method: "POST",
				body: formData,
				headers: {
					"x-csrftoken": window.CSRF_TOKEN,
					"x-requested-with": "XMLHttpRequest",
				},
			})
				.then((res: Response) => {
					return res.json();
				})
				.then(({ stream: newStream }: { stream: FellowStream }) => {
					if (stream) newStream.notes = stream.notes.concat(newStream.notes);
					setStream(newStream);
					const customEmojiInstance = CustomEmojiManager.getInstance();
					if (!customEmojiInstance.originalAccountId) {
						CustomEmojiManager.getInstance().init([], newStream.account.id, {});
					}
					setLoading(false);
					setOffset(offset + 5);
					callback?.();
					return newStream;
				})
				.catch(() => {
					setLoading(false);
					callback?.();
				});
		},
		[offset, stream],
	);

	React.useEffect(() => {
		fetchNotes();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const onProceed = React.useCallback(() => {
		setAcceptedRisk(true);
	}, [setAcceptedRisk]);

	const titleMatchesStreamTitle = React.useCallback(
		(noteTitle: string) => Boolean(noteTitle && stream?.title.text === noteTitle),
		[stream?.title.text],
	);

	if (loading) return <Loader loading={loading} />;
	if (!stream) return <NotFound />;

	if (stream.hasSafeBrowsingThreats && !acceptedRisk) {
		return (
			<div className={styles.riskState}>
				<div className={styles.riskWarning}>
					<FontAwesomeIcon icon={faExclamationCircle} />
					<h3>These notes have been flagged as unsafe</h3>
					<h5>
						They may contain spam, malware, phishing or illegal content. If you do not know the person who sent you this
						link, we suggest you close this tab.{" "}
						<ButtonLink onClick={onProceed} className={styles.proceedButton}>
							Proceed anyway
						</ButtonLink>
					</h5>
					<div className={styles.links}>
						<HelpCenterLink articleId="5484793">Learn more</HelpCenterLink>
						<ContactSupportLink>Send us a message</ContactSupportLink>
						<a rel="noopener" target="_blank" href="https://fellow.app/terms-of-use/">
							Terms of use
						</a>
					</div>
				</div>
			</div>
		);
	}

	return (
		<ConnectivityProvider websocketClient={WebsocketClient.getInstance()}>
			<Header workspace={stream.account} streamId={props.match.params.streamId} inviteId={inviteId} />
			<div className={styles.streamContainer}>
				<header className={styles.header}>
					<h1>{stream.title.text}</h1>
					<p>
						<FontAwesomeIcon icon={faEye} />
						This is a view-only document
					</p>
				</header>
				<div className={styles.notesContainer}>
					{stream.notes.length > 0 ? (
						<>
							{/* Blur notes if an invite ID is present */}
							{inviteId ? (
								<NoteBlur inviteId={inviteId}>
									{stream.notes.map(note => (
										<Note
											note={note}
											publicLinkId={props.match.params.streamId}
											inviteId={inviteId}
											key={note.createdAt}
											account={stream.account}
											streamTitleMatchesNoteTitle={titleMatchesStreamTitle(note.title)}
										/>
									))}
								</NoteBlur>
							) : (
								stream.notes.map(note => (
									<Note
										note={note}
										publicLinkId={props.match.params.streamId}
										inviteId={inviteId}
										key={note.createdAt}
										account={stream.account}
										streamTitleMatchesNoteTitle={titleMatchesStreamTitle(note.title)}
									/>
								))
							)}
							{stream.history.paywalled && (
								<div className="notes-stream-card note">
									<div className="main">
										<div className={paywallStyles.paywallWrapper}>
											<div className={paywallStyles.paywallContainer}>
												<div className={paywallStyles.paywallContent}>
													<div>
														<AccessDenied />
													</div>
													<div>
														<span>
															You do not have access to notes that were created over {stream.history.limit} days ago (
															{moment(stream.history.paywallDate).format("MMM Do, YYYY")}).
														</span>
														&nbsp;
														<span>
															<Popup
																inverted
																size="tiny"
																content="This workspace needs to be upgraded to access unlimited note history."
																position="bottom center"
																trigger={<FontAwesomeIcon icon={faInfoCircle} />}
															/>
														</span>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							)}
							{stream.hasNotesRemaining && !stream.history.paywalled && (
								<LoadMoreButton dotted={false} loadMore={fetchNotes} fireOnMount={false} debounceTimeout={500} />
							)}
						</>
					) : (
						<div className={styles.emptyState}>
							<p>There aren&apos;t any notes here yet.</p>
						</div>
					)}
				</div>
			</div>
		</ConnectivityProvider>
	);
}
